<script>
  // @ts-check
  import textFit from "textfit";
  import { onMount } from "svelte";
  import { v4 as uuid } from "@lukeed/uuid";

  import { getTheme } from "../utilities/themes";
  import { debounce } from "../utilities/performance";
  import {
    Breakpoints,
    getBreakpoint,
    maxFontSizeMapForFullWidthText
  } from "../utilities/breakpoints";

  import Image from "../atoms/Image.svelte";
  import GridContainer from "../atoms/GridContainer.svelte";
  import Grid from "../atoms/Grid.svelte";
  import Link from "../atoms/Link.svelte";
  import DynamicHeading from "../atoms/DynamicHeading.svelte";
  import ContentfulVideo from "../molecules/ContentfulVideo.svelte";

  /** Primarily used for testing @type {string} */
  export let id = undefined;

  /**
   * Additional classes which apply to the container
   * @type { string }
   * */
  let className = "";
  export { className as class };

  /**
   * The title of the hero section
   * @type {string}
   */
  export let title;

  /**
   * The subtitle of the hero section
   * @type {string}
   */
  export let subtitle = null;

  /**
   * The call to action in the hero section
   * @type {import('../types/shared.js').CTA}
   */
  export let cta;

  /**
   * The featured image in the hero section
   * @type {import('../types/shared.js').Image}
   */
  export let featuredImage;

  /**
   * The theme of the hero section
   * @type {import('../utilities/themes.js').ThemeName}
   */
  export let theme;

  /**
   * Flag to determine if the text size is variable (all caps, using textfit)
   * @type {boolean}
   */
  export let hasVariableSizeText = false;

  /**
   * The contentful video in the hero section, different from the featured video
   * @type {import('../types/shared.js').ContentfulVideo}
   */

  export let contentfulVideo;
  let currentTheme = getTheme(theme);

  /** @type {HTMLHeadingElement} */
  let titleEl;

  let animate = !hasVariableSizeText;
  let titleFontSize = null;
  let titleWidth = null;

  onMount(() => {
    initTextFit();

    // TODO: Find a better solution
    setTimeout(() => {
      animate = true;
    }, 500);
  });

  const initTextFit = () => {
    if (!hasVariableSizeText) {
      return;
    }

    const triggerTextFit = async () => {
      if (!titleEl) {
        return;
      }

      // Reset element style (in case of multiline change)
      titleEl?.setAttribute("style", "");

      const breakpoint = getBreakpoint(window.innerWidth);
      const multiLine = breakpoint === Breakpoints.sm;
      const maxFontSize = maxFontSizeMapForFullWidthText[breakpoint];

      textFit(titleEl, {
        minFontSize: 24,
        maxFontSize,
        widthOnly: true,
        detectMultiLine: false,
        reProcess: true,
        multiLine
      });
    };

    // Wait for fonts to load before triggering textfit
    document.fonts.onloadingdone = async () => {
      await triggerTextFit();
    };

    const debouncedTriggerTextFit = debounce(triggerTextFit, 100);

    const recalculateTextFit = () => {
      debouncedTriggerTextFit();

      if (titleEl && titleEl.children[0]) {
        titleFontSize = window.getComputedStyle(titleEl.children[0]).fontSize;
        titleWidth = titleEl.children[0].clientWidth;
      }
    };

    // We need to recalculate text fit when the title element size changes (e.g. window resize)
    const resizeObserver = new ResizeObserver(() => {
      recalculateTextFit();
    });

    resizeObserver.observe(titleEl);

    // We also need to recalculate text fit when the title element content changes (e.g. textfit creates <span> child element)
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          recalculateTextFit();
        }
      });
    });

    let mutationConfig = {
      childList: true,
      subtree: true,
      characterData: true
    };

    mutationObserver.observe(titleEl, mutationConfig);
  };

  const splitTitle = title.trim().split(" ");

  const titleClass = hasVariableSizeText ? "heading-xxl uppercase" : "heading-xl md:col-span-9";
  const transitionContainerClass =
    "transition-all duration-[400ms] delay-[750ms] ease-[cubic-bezier(0.33, 1, 0.68, 1)]";

  $: animateContainer = animate ? "translate-y-0" : "translate-y-[7%] md:translate-y-[20%]";
  $: animateTitleClass = animate ? "[&>div>span]:translate-y-0" : `[&>div>span]:translate-y-full`;
  $: animateOpacity = animate ? "opacity-100" : "opacity-transparent";
  $: titleFontSizeClass = `font-size: ${titleFontSize}`;
  $: titleWidthClass = `width: ${titleWidth}px`;
</script>

<GridContainer
  {id}
  containerElement="section"
  containerClass="{currentTheme?.textColor} {currentTheme?.backgroundColor} transition-all duration-[1000ms] ease-[cubic-bezier(0.33, 1, 0.68, 1)] overflow-hidden pt-15 pb-7.5 md:pt-20 lg:pb-15 xl:pt-25"
>
  <Grid gridClass="col-full-width {transitionContainerClass} {animateContainer} {className}">
    <div
      bind:this={titleEl}
      aria-hidden={true}
      class="invisible col-full-width w-full leading-[0] {titleClass}"
    >
      {title}
    </div>
    <DynamicHeading aria-label={title} class="col-full-width w-full {titleClass}">
      {#if hasVariableSizeText}
        <div
          class="{animateTitleClass} overflow-hidden md:flex md:justify-between"
          style={titleWidthClass}
        >
          {#each splitTitle as title, index (`${title}-${uuid()}`)}
            <div class="overflow-y-clip" aria-hidden={true}>
              <span
                class="block transition-all duration-[300ms] ease-spring-easing"
                style="transition-delay: {index * 100 + 400}ms; {titleFontSizeClass}">{title}</span
              >
            </div>
          {/each}
        </div>
      {:else}
        {title}
      {/if}
    </DynamicHeading>

    {#if hasVariableSizeText && subtitle}
      <div
        class="subtitle body-lg col-full-width overflow-hidden pt-5 lg:pt-7 {transitionContainerClass} {animateOpacity}"
      >
        {subtitle}
        {#if cta}
          <Link
            class="block max-md:pt-5 md:inline-flex md:pl-2"
            href={cta.url}
            isExternal={cta.isExternal}
            label={cta.text}
            variant="large"
            {theme}
            id={cta.id}
          />
        {/if}
      </div>
    {:else if cta}
      <Link
        class="col-full-width text-right max-md:pt-5 md:col-span-3 md:col-start-10 md:self-end md:justify-self-end {transitionContainerClass} {animateOpacity}"
        href={cta.url}
        isExternal={cta.isExternal}
        label={cta.text}
        variant="large"
        {theme}
        id={cta.id}
      />
    {/if}
    {#if contentfulVideo}
      <ContentfulVideo
        src={contentfulVideo.videoUrl}
        poster={contentfulVideo.posterImageUrl}
        videoTitle={contentfulVideo.videoTitle}
        containerClass="col-full-width pt-7 lg:pt-12.5"
      />
    {:else}
      <Image
        {...featuredImage}
        aspectRatioClasses="aspect-3/4 md:aspect-3/2"
        containerClass="col-full-width pt-7 lg:pt-12.5 {transitionContainerClass} {animateOpacity}"
      />
    {/if}
  </Grid>
  <!--
    TODO: Add support for rich text / videos: https://github.com/ObamaFoundation/of-design-system/issues/89
  -->
</GridContainer>
